import React, { useState, useEffect } from 'react';
import { Download, X, Share } from 'lucide-react';
import { Button } from './ui/button';

export function InstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return; // Already installed as PWA
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user dismissed the banner before
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Listen for beforeinstallprompt (Chrome, Edge, Samsung)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show banner after delay
    if (iOS) {
      setTimeout(() => setShowBanner(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setInstallPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  // iOS-specific instructions modal
  if (isIOS && showIOSInstructions) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold text-slate-900">
              დააინსტალირე PriceMap
            </h3>
            <button 
              onClick={() => setShowIOSInstructions(false)}
              className="p-1 hover:bg-slate-100 rounded-full"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <span className="text-sm font-medium text-blue-600">1</span>
              </div>
              <div>
                <p className="text-slate-700">
                  დააჭირე <Share className="w-4 h-4 inline text-blue-500" /> ღილაკს Safari-ის ქვედა პანელში
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <span className="text-sm font-medium text-blue-600">2</span>
              </div>
              <div>
                <p className="text-slate-700">
                  გამოჩნდება მენიუ — ქვევით გადაახვიე და აირჩიე <strong>"Add to Home Screen"</strong>
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <span className="text-sm font-medium text-blue-600">3</span>
              </div>
              <div>
                <p className="text-slate-700">
                  დააჭირე <strong>"Add"</strong> ზედა მარჯვენა კუთხეში
                </p>
              </div>
            </div>
          </div>
          
          <Button 
            onClick={() => setShowIOSInstructions(false)}
            className="w-full mt-6"
          >
            გასაგებია
          </Button>
        </div>
      </div>
    );
  }

  // Install banner
  return (
    <div 
      className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-40 animate-slide-up"
      data-testid="install-prompt"
    >
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shrink-0">
            <Download className="w-6 h-6 text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-slate-900 text-sm">
              დააინსტალირე PriceMap
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">
              სწრაფი წვდომა ტელეფონიდან
            </p>
          </div>
          
          <button 
            onClick={handleDismiss}
            className="p-1 hover:bg-slate-100 rounded-full shrink-0"
            aria-label="დახურვა"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        
        <div className="flex gap-2 mt-3">
          {isIOS ? (
            <Button 
              onClick={() => setShowIOSInstructions(true)}
              size="sm"
              className="flex-1"
            >
              როგორ დავაინსტალირო
            </Button>
          ) : (
            <Button 
              onClick={handleInstall}
              size="sm"
              className="flex-1"
            >
              დაინსტალირება
            </Button>
          )}
          <Button 
            onClick={handleDismiss}
            variant="outline"
            size="sm"
          >
            არა
          </Button>
        </div>
      </div>
    </div>
  );
}

export default InstallPrompt;
